/**
 * Season-based collection logic (CET). See .cursor/.agents/ommi-style-director.md and data/seasons.ts.
 */

import { computed } from 'vue'
import {
  getCETDateParts,
  getCurrentSeasonIndex,
  getNextSeasonLaunchCET,
  getSeasonPeriodLabel,
  type SeasonIndex,
} from '~/data/seasons'
import type { CollectionStatus } from '~/data/collections'

export function useSeasons() {
  const currentSeasonIndex = computed<SeasonIndex>(() => getCurrentSeasonIndex(new Date()))

  const nextSeasonLaunch = computed(() => getNextSeasonLaunchCET(new Date()))

  const cetYear = computed(() => getCETDateParts(new Date()).year)

  function getSeasonStatus(seasonIndex: SeasonIndex): CollectionStatus {
    const current = currentSeasonIndex.value
    if (seasonIndex === current) return 'active'
    const diff = (seasonIndex - current + 4) % 4
    return diff === 1 ? 'upcoming' : 'past'
  }

  /** Year to use for period label: season 4 (Winter) starts in Dec so use previous year. */
  function getSeasonPeriodYear(seasonIndex: SeasonIndex): number {
    return seasonIndex === 4 ? cetYear.value - 1 : cetYear.value
  }

  function getSeasonPeriod(seasonIndex: SeasonIndex, year?: number, locale?: string): string {
    const y = year ?? getSeasonPeriodYear(seasonIndex)
    return getSeasonPeriodLabel(seasonIndex, y, locale ?? 'en')
  }

  return {
    currentSeasonIndex,
    nextSeasonLaunch,
    cetYear,
    getSeasonStatus,
    getSeasonPeriod,
    getSeasonPeriodYear,
  }
}
